// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import {get} from 'lodash';

// Components
import Button from '../core/button';
import DisplayPathForm from './display-path-form';
import Error from '../core/error';
import FormConfirm from '../core/form-confirm';
import Loading from '../core/loading';
import LockedIndicator from './locked-indicator';
import Permissions from '../core/permissions';
import RecordLocker from './record-locker';

// Containers
import withForm from '../../containers/with-form';
import withPermissions from '../../containers/with-permissions';
import Deletable from '../../containers/deletable';

// Styles
import './edit-display-path.scss';

const formName = 'editDisplayPath';

function EditDisplayPath({
  formData,
  hasAccessByRole,
  locallyLocked,
  lockedRecords,
  resetForm,
  searchFetching,
  setFieldValue,
  searchValues: { networkDisplayPathId },
}) {
  const getFormProps = () => ({
    className: 'edit-display-path',
    editing: true,
    formData,
    formName,
    setFieldValue,
    onReset: (submitted, resetValues) => resetForm(submitted, resetValues, true),
  });

  const getDeletableProps = () => ({
    recordId: networkDisplayPathId,
    formName: 'deleteDisplayPath',
  });

  const getFormConfirmProps = ({
    deleteCompleted,
    handleConfirmClick,
    handleResetClick,
    resetDelete,
    status: { error, fetching, response },
  }) => ({
    completed: deleteCompleted,
    confirmMessage: 'Are you sure you want to delete this display path?',
    confirmLabel: 'Delete',
    error,
    fetching,
    formSubmitReset: resetDelete,
    onComplete: handleResetClick,
    onCancel: resetDelete,
    onConfirm: handleConfirmClick,
    response,
    searchData: get(formData, 'values', {}),
    whitelistKeys: ['networkDisplayPathId', 'displayPath', 'status', 'adult', 'adultLevel'],
  });

  const setLockAndRenderIcon = () => (networkDisplayPathId ? (
            <div className="edit-display-path--lock">
                <Permissions
                    allowRoles={['networkDisplayPath_update', 'networkDisplayPath_delete']}
                    notAuthorizedMessage={false}
                >
                    <RecordLocker recordId={networkDisplayPathId} serviceKey="displayPaths" />
                </Permissions>
                <LockedIndicator
                    detail={true}
                    lockId={networkDisplayPathId}
                    recordLabel="Display Path"
                    serviceKey="displayPaths"
                />
            </div>
        ) : null);

  const isDisplayPathIdLockedByOtherUser = () => {
    const lockedIdByOtherUsers = get(lockedRecords, 'displayPaths', [])
      .filter((lockedRecord) => !get(locallyLocked, 'displayPaths', []).includes(lockedRecord.recordId))
      .map((lockedRecord) => lockedRecord.recordId);

    return lockedIdByOtherUsers && lockedIdByOtherUsers.includes(networkDisplayPathId);
  };

  const renderDeleteButton = (deletable) => {
    const {
      handleDeleteClick,
      status: { fetching },
      shouldRenderDeleteButton,
    } = deletable;

    return hasAccessByRole({ allowRoles: ['networkDisplayPath_delete'] })
            && !fetching
            && shouldRenderDeleteButton() ? (
            <div className="edit-display-path--buttons">
                <Button className="edit-display-path--button" onClick={handleDeleteClick}>
                    Delete
                </Button>
            </div>
      ) : null;
  };

  return (
        <Permissions
            allowRoles={['networkDisplayPath_update', 'networkDisplayPath_delete']}
            notAuthorizedMessage={true}
        >
            {networkDisplayPathId ? (
                <>
                    {setLockAndRenderIcon()}
                    {!isDisplayPathIdLockedByOtherUser() ? (
                        <Deletable {...getDeletableProps()}>
                            {(deletable) => (
                                <div className="edit-display-path">
                                    {renderDeleteButton(deletable)}
                                    {deletable.shouldRenderConfirm() ? (
                                        <FormConfirm {...getFormConfirmProps(deletable)} />
                                    ) : (
                                        <Permissions
                                            allowRoles={['networkDisplayPath_update']}
                                            notAuthorizedMessage={false}
                                        >
                                            <DisplayPathForm {...getFormProps()} />
                                        </Permissions>
                                    )}
                                    <Loading loading={deletable.status.fetching} />
                                </div>
                            )}
                        </Deletable>
                    ) : null}
                </>
            ) : (
                <Error className="edit-display-path--message" error="Select a display path from the list." />
            )}
            <Loading loading={searchFetching} />
        </Permissions>
  );
}

EditDisplayPath.propTypes = {
    formData: PropTypes.object.isRequired,
    hasAccessByRole: PropTypes.func.isRequired,
    locallyLocked: PropTypes.object,
    lockedRecords: PropTypes.object,
    resetForm: PropTypes.func.isRequired,
    searchFetching: PropTypes.bool,
    searchValues: PropTypes.object,
    setFieldValue: PropTypes.func,
};

export default withPermissions(withForm(EditDisplayPath, formName));
